import { BarChart } from '@toggle/design-system';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { SectionTitle } from '~/components/section-title/SectionTitle';
import { useSnakesByNames } from '~/hooks/use-snake/useSnake';

import * as S from './ValuationMetrics.styles';

const N_DAYS = 1;

export interface ValuationMetricsProps {
  snakeName: string;
  companies: {
    tag: string;
    formattedTicker: string;
    color: string;
  }[];
  chartWidth: number;
  chartHeight: number;
  setValuationError: (value: boolean) => void;
  refetchTrigger?: number;
}

export const ValuationMetrics = ({
  snakeName,
  companies,
  chartWidth,
  chartHeight,
  setValuationError,
  refetchTrigger,
}: ValuationMetricsProps) => {
  const { t } = useTranslation('widget');

  const snakeData = companies.map(company => ({
    snakeName: `${company.tag}.${snakeName}`,
    nDays: N_DAYS,
  }));

  const {
    data: snakesData,
    isLoading,
    isError,
    refetch,
  } = useSnakesByNames(snakeData);

  useEffect(() => {
    if (isError) {
      setValuationError(true);
    }
  }, [isError]);

  useEffect(() => {
    if (refetchTrigger) {
      refetch();
    }
  }, [refetchTrigger]);

  if (isLoading) {
    return (
      <S.SkeletonBarChartLoader
        data-testid="valuation-metric-loading"
        areas={[
          {
            styles: {
              width: 100,
              height: 24,
              marginBottom: 12,
              borderRadius: '8px',
            },
          },
          {
            styles: {
              width: chartWidth,
              height: chartHeight,
              borderRadius: '8px',
            },
          },
        ]}
      />
    );
  }

  if (!snakesData.filter(Boolean).length) {
    return null;
  }

  const chartData = snakesData.map((data, index) => ({
    value: data?.[0].value ? data?.[0].value / 100 : undefined,
    label: companies[index].formattedTicker,
    color: companies[index].color,
  }));

  if (!chartData.length) {
    return null;
  }

  return (
    <div data-testid="valuation-metric">
      {/* i18next-extract-disable-next-line */}
      <SectionTitle
        title={t(`widget:assetOverview.snakeNames.${snakeName}`)}
        variant="small"
      />
      <BarChart
        data={chartData}
        height={chartHeight}
        width={chartWidth}
        showXAxisLabels={false}
      />
    </div>
  );
};
